import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Trophy, ChevronRight } from 'lucide-react';
import { ROUTES } from '@/lib/constants';
import { CLUBS as TID } from '@/constants/testIds';
import { buildAssetUrl } from '@/lib/api';

/**
 * Tarjeta de club / canal OTT.
 * Mobile-first. Click → /clubes/:slug.
 */
export function ClubCard({ club }) {
  const cover = buildAssetUrl(club.cover_image_url);
  const logo = buildAssetUrl(club.logo_url);
  // Ubicación en dos líneas: ciudad arriba, provincia debajo. Mantiene el
  // icono de MapPin delante de la primera línea. Ninguna de las dos se
  // trunca con "..." sobre la otra (ambas usan truncate individual).
  const cityLine = club.city || '';
  const provinceLine = club.province || '';
  const hasLocation = Boolean(cityLine || provinceLine);
  return (
    <Card
      data-testid={TID.card(club.slug)}
      className="group overflow-hidden border-[var(--ns-border)] hover:border-[var(--ns-gold)]/40 hover:shadow-[0_12px_36px_rgba(28,22,18,0.08)] transition-all duration-200 rounded-2xl bg-white"
    >
      <Link to={ROUTES.clubDetail(club.slug)} className="block">
        <div className="relative h-32 sm:h-36 bg-gradient-to-br from-[var(--ns-gold)]/30 to-[var(--ns-gold-600)]/25 overflow-hidden">
          {cover ? (
            <img
              src={cover}
              alt={`Portada ${club.name}`}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[var(--ns-gold-600)]/20 to-transparent" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
          {club.sport_name ? (
            <Badge
              className="absolute top-3 right-3 bg-white/90 text-[var(--ns-text)] border border-[var(--ns-border)] backdrop-blur-sm text-[11px] font-semibold uppercase tracking-wider"
              variant="secondary"
            >
              {club.sport_name}
            </Badge>
          ) : null}
        </div>
        <div className="p-4 sm:p-5 pt-3 sm:pt-4">
          <div className="flex items-start gap-3">
            {/* Logo del club: completamente dentro de la zona blanca (sin
                negative margin → sin solape con la portada); +~12-14 % de
                tamaño sobre el valor anterior manteniendo object-contain
                y el centrado dentro del contenedor. */}
            <div className="h-16 w-16 sm:h-[72px] sm:w-[72px] rounded-2xl bg-white border border-[var(--ns-border)] shadow-sm overflow-hidden flex items-center justify-center shrink-0 p-1.5 sm:p-2">
              {logo ? (
                <img
                  src={logo}
                  alt={`Logo ${club.name}`}
                  className="max-h-[82%] max-w-[82%] object-contain"
                  loading="lazy"
                  draggable={false}
                />
              ) : (
                <Trophy className="h-7 w-7 text-[var(--ns-gold-600)]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-base sm:text-lg leading-tight text-[var(--ns-text)] truncate">
                {club.name}
              </h3>
              {hasLocation ? (
                <div
                  className="mt-0.5 flex items-start gap-1 text-xs text-[var(--ns-text-muted)]"
                  data-testid={`club-card-location-${club.slug}`}
                >
                  <MapPin className="h-3.5 w-3.5 text-[var(--ns-gold-600)] shrink-0 mt-[2px]" />
                  <div className="min-w-0 leading-tight">
                    {cityLine ? (
                      <p
                        className="break-words line-clamp-2"
                        data-testid={`club-card-city-${club.slug}`}
                      >
                        {cityLine}
                      </p>
                    ) : null}
                    {provinceLine ? (
                      <p
                        className="truncate"
                        data-testid={`club-card-province-${club.slug}`}
                      >
                        {provinceLine}
                      </p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          {club.description ? (
            <p className="mt-3 text-sm text-[var(--ns-text-muted)] line-clamp-2 leading-relaxed">
              {club.description}
            </p>
          ) : null}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-[var(--ns-text-subtle)]">
              {club.teams_count || 0}{' '}
              {club.teams_count === 1 ? 'equipo' : 'equipos'}
            </span>
            <Button
              asChild
              size="sm"
              variant="ghost"
              className="h-8 px-2 text-[var(--ns-gold-700)] hover:text-[#1c1612] hover:bg-[var(--ns-gold)]/20"
              data-testid={TID.cardCta(club.slug)}
            >
              <span className="inline-flex items-center gap-1 font-semibold">
                Ver canal
                <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </span>
            </Button>
          </div>
        </div>
      </Link>
    </Card>
  );
}

export function ClubCardSkeleton() {
  return (
    <div className="rounded-2xl border border-[var(--ns-border)] bg-white overflow-hidden">
      <div className="h-32 sm:h-36 bg-[var(--ns-bg-2)] animate-pulse" />
      <div className="p-4 sm:p-5 pt-3 sm:pt-4">
        <div className="flex items-start gap-3">
          <div className="-mt-9 sm:-mt-10 h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-[var(--ns-bg-2)] animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-3/4 bg-[var(--ns-bg-2)] rounded animate-pulse" />
            <div className="h-3 w-1/2 bg-[var(--ns-bg-2)] rounded animate-pulse" />
          </div>
        </div>
        <div className="mt-3 space-y-2">
          <div className="h-3 w-full bg-[var(--ns-bg-2)] rounded animate-pulse" />
          <div className="h-3 w-5/6 bg-[var(--ns-bg-2)] rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}
